/**
 * CreateMemberModal - 회원을 새로 등록하는 모달 (instructor-web)
 *
 * - 이름(필수), 연락처(필수) 입력
 * - centerName은 부모가 activeMembership에서 전달 (백엔드 lookupCenterByName 입력)
 * - useCreateMember 훅으로 Cloud Function `createInstructorMember` 호출
 * - 성공 시 onCreated 콜백 → 부모가 모달을 닫고 목록을 refetch
 */

import { useState, useEffect } from "react";
import type { FormEvent } from "react";
import { Card, Input, Button, Alert } from "@vlab-corporation/design-system";
import { useCreateMember, type CreateMemberParams } from "@/hooks/useCreateMember";

interface CreateMemberModalProps {
  isOpen: boolean;
  /** 현재 센터 이름 (activeMembership.centerName) */
  centerName: string;
  onClose: () => void;
  onCreated: () => void;
}

/** 연락처를 숫자만 추출한 정규화된 값으로 변환 */
function normalizeMobile(input: string): string {
  return input.replace(/[^0-9]/g, "");
}

/** 백엔드 contract와 동일한 길이 규칙(10~11자리) */
function isValidMobile(normalized: string): boolean {
  return normalized.length === 10 || normalized.length === 11;
}

/** mutation.error의 message를 안전하게 추출 */
function getErrorMessage(error: unknown): string | null {
  if (error && typeof error === "object" && "message" in error) {
    const msg = (error as { message: unknown }).message;
    if (typeof msg === "string" && msg.length > 0) {
      return msg;
    }
  }
  return null;
}

export function CreateMemberModal({
  isOpen,
  centerName,
  onClose,
  onCreated,
}: CreateMemberModalProps) {
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);

  const createMember = useCreateMember();

  // 모달이 열릴 때 폼 초기화
  useEffect(() => {
    if (isOpen) {
      setName("");
      setMobile("");
      setValidationError(null);
      createMember.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const trimmedName = name.trim();
    const normalizedMobile = normalizeMobile(mobile);

    if (!trimmedName) {
      setValidationError("이름을 입력해주세요.");
      return;
    }
    if (!normalizedMobile) {
      setValidationError("연락처를 입력해주세요.");
      return;
    }
    if (!isValidMobile(normalizedMobile)) {
      setValidationError("연락처는 숫자 10~11자리로 입력해주세요.");
      return;
    }

    setValidationError(null);

    const params: CreateMemberParams = {
      centerName,
      name: trimmedName,
      mobile: normalizedMobile,
    };

    createMember.mutate(params, {
      onSuccess: () => {
        onCreated();
      },
    });
  };

  const isSubmitDisabled = !name.trim() || !mobile.trim() || createMember.isPending;
  const serverErrorMessage = createMember.isError ? getErrorMessage(createMember.error) : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
      <Card className="w-full max-w-md p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">새 회원 등록</h2>
        <p className="text-sm text-gray-500 mb-4">센터: {centerName}</p>

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <Input
            label="이름"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="회원 이름"
          />
          <Input
            label="연락처"
            type="tel"
            inputMode="numeric"
            value={mobile}
            onChange={(e) => setMobile(normalizeMobile(e.target.value))}
            placeholder="예: 01012345678"
          />

          {validationError && <Alert variant="error">{validationError}</Alert>}
          {createMember.isError && !validationError && (
            <Alert variant="error">
              {serverErrorMessage ?? "회원 등록에 실패했습니다. 다시 시도해주세요."}
            </Alert>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              취소
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitDisabled}>
              {createMember.isPending ? "등록 중..." : "등록"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
